import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  useRecordContext,
  useDataProvider,
  useRefresh,
  useNotify,
  useListContext,
  Link,
} from 'react-admin';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { X } from 'lucide-react';
import { logger } from '@musedrops/shared';
import { clearAllQueueJobs, clearProcessLogs } from '../../../data/process';
import { supabase } from '../../../services/supabase';
import { BlobLogsTab } from '../components/BlobLogsTab';
import { JobActionsMenu } from '../components/JobActionsMenu';
import {
  DateTimeField,
  formatDateTime,
  formatDuration,
} from '../../../shared/components/DateTimeField';
import { PageToolbar } from '../../../shared/components/PageToolbar';
import { SearchInputEnterOnly } from '../../../shared/components/SearchInputEnterOnly';
import { FilterSelectInput } from '../../../shared/components/FilterSelectInput';

// Event type emoji mapping
const EVENT_TYPE_CHOICES = [
  { id: 'started', name: '▶️ Started' },
  { id: 'completed', name: '✅ Completed' },
  { id: 'error', name: '‼️ Error' },
  { id: 'retry', name: '🔄 Retry' },
];

// Issue #847: canonical process names (= entry Edge Function names)
const PROCESS_CHOICES = [
  { id: 'gen-story-outline', name: 'Story Outline' },
  { id: 'gen-story-embedding', name: 'Story Embedding' },
  { id: 'gen-story-image', name: 'Story Image' },
  { id: 'gen-story-announce', name: 'Story Announce' },
  { id: 'gen-story-script', name: 'Story Script' },
  { id: 'gen-story-voice', name: 'Story Voice' },
  { id: 'gen-story-mixed-audio', name: 'Audio Mix' },
  { id: 'gen-story-mixed-audio-external', name: 'Audio Mix (External)' },
  { id: 'gen-story-ideas', name: 'Story Ideas' },
  { id: 'gen-story-translation', name: 'Story Translation' },
  { id: 'gen-show-profile', name: 'Show Profile' },
  { id: 'gen-show-ideas', name: 'Show Ideas' },
  { id: 'gen-show-image', name: 'Show Image' },
  { id: 'gen-show-translation', name: 'Show Translation' },
  { id: 'gen-show-announce', name: 'Show Announce' },
  { id: 'gen-show-theme-music', name: 'Show Theme Music' },
];

// Custom component to display event type with emoji
const EventTypeField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const eventChoice = EVENT_TYPE_CHOICES.find(
    choice => choice.id === record.eventType
  );
  return <span>{eventChoice?.name || record.eventType}</span>;
};
EventTypeField.displayName = 'Event Type';

// Custom component to display entity type and name with link
const EntityField = () => {
  const record = useRecordContext();

  if (!record) return null;

  // Use camelCase since dataProvider transforms keys
  const { entityType, entityId, entityName } = record;

  // Handle jobs without entity (null entity_type/entity_id) - show "N/A" with no link
  if (!entityType || !entityId) {
    return <span style={{ color: '#666', fontStyle: 'italic' }}>N/A</span>;
  }

  // Determine if this is a story-related or show-related entity type
  // story, story_translation, story_trans, story_announce → stories (📗)
  // show, show_translation, show_announce → shows (📣)
  const isStoryRelated = entityType === 'story' || entityType === 'story_translation' || entityType === 'story_trans' || entityType === 'story_announce';
  const emoji = isStoryRelated ? '📗' : '📣';
  const entityPath = isStoryRelated ? 'stories' : 'shows';

  // For translation/announce types, entity_id is "uuid:lang" - extract the UUID for linking
  // story_trans uses plain UUID (lang is in separate column)
  // For regular types, entity_id is just the UUID
  const isCompositeId = entityType.includes('_translation') || entityType === 'show_announce' || entityType === 'story_announce';
  const linkId = isCompositeId ? entityId.split(':')[0] : entityId;

  // Extract lang for tab deep-linking: composite IDs have lang after ":", story_trans has it in separate column
  const lang = isCompositeId ? entityId.split(':')[1] : (entityType === 'story_trans' ? record.lang : null);
  const tabParam = lang ? `?tab=${lang}` : '';

  // Display entity name if available, otherwise fall back to full entity ID (not truncated)
  const displayText = entityName || entityId;

  return (
    <Link to={`/${entityPath}/${linkId}${tabParam}`}>
      {emoji} {displayText}
    </Link>
  );
};
EntityField.displayName = 'Entity';

// Custom component to display message with truncation and expand/collapse
const MessageField = () => {
  const record = useRecordContext();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!record || !record.message) return null;

  const { message } = record;
  const maxLength = 100;

  if (message.length <= maxLength) {
    return <span>{message}</span>;
  }

  if (isExpanded) {
    return (
      <span>
        {message}{' '}
        <button
          type="button"
          style={{
            color: '#1976d2',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
          }}
          onClick={e => {
            e.stopPropagation();
            setIsExpanded(false);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(false);
            }
          }}
          aria-label="Collapse message"
        >
          ◀︎◀︎
        </button>
      </span>
    );
  }

  return (
    <span>
      {message.slice(0, maxLength)}{' '}
      <button
        type="button"
        style={{
          color: '#1976d2',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
        }}
        onClick={e => {
          e.stopPropagation();
          setIsExpanded(true);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setIsExpanded(true);
          }
        }}
        aria-label="Expand message"
      >
        ▶︎▶︎
      </button>
    </span>
  );
};
MessageField.displayName = 'Message';

// Custom filters for the process logs
const ProcessLogFilters = [
  <SearchInputEnterOnly
    key="q"
    source="q"
    placeholder="Search entity ID, process, or message"
    alwaysOn
  />,
  <FilterSelectInput
    key="eventType"
    source="eventType"
    label="Event Type"
    choices={EVENT_TYPE_CHOICES}
    alwaysOn
  />,
  <FilterSelectInput
    key="process"
    source="process"
    label="Process"
    choices={PROCESS_CHOICES}
    alwaysOn
  />,
];

// Cancel button component
const CancelJobButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  if (!record) return null;

  const handleCancel = async () => {
    try {
      const statusUpdate =
        record.status === 'processing' ? 'cancelled' : 'cancelled';
      const message =
        record.status === 'processing'
          ? 'Running job cancelled successfully'
          : 'Job cancelled successfully';

      await dataProvider.update('process_queue', {
        id: record.id,
        data: { status: statusUpdate },
        previousData: record,
      });
      notify(message, { type: 'success' });
      refresh();
    } catch (error) {
      logger.error('Cancel error', {
        jobId: record.id,
        status: record.status,
        error,
      });
      notify('Failed to cancel job', { type: 'error' });
    }
  };

  const buttonTitle =
    record.status === 'processing' ? 'Cancel running job' : 'Cancel job';
  const buttonColor =
    record.status === 'processing'
      ? '#d32f2f' // Red for running jobs
      : '#757575'; // Gray for pending jobs

  return (
    <IconButton
      onClick={handleCancel}
      size="small"
      title={buttonTitle}
      sx={{ color: buttonColor }}
    >
      <X size={16} />
    </IconButton>
  );
};
CancelJobButton.displayName = 'Actions';

// Custom component to display "Run in" with proper formatting
// For processing jobs, shows live elapsed time: "🔷 Running (45s)"
const RunInField = () => {
  const record = useRecordContext();
  const [elapsed, setElapsed] = useState<number | null>(null);

  // Extract values for effect dependencies
  const jobId = record?.id;
  const status = record?.status;
  const startedAt = record?.startedAt;

  useEffect(() => {
    if (!status || status !== 'processing' || !startedAt) {
      setElapsed(null);
      return undefined;
    }

    const calculateElapsed = () => {
      const startTime = new Date(startedAt).getTime();
      return Date.now() - startTime;
    };

    setElapsed(calculateElapsed());

    const interval = setInterval(() => {
      setElapsed(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [jobId, status, startedAt]);

  if (!record) return null;

  // Processing jobs: show "Running (elapsed)"
  if (record.status === 'processing') {
    return (
      <span style={{ color: '#1976d2', fontWeight: 500 }}>
        🔷 Running ({formatDuration(elapsed)})
      </span>
    );
  }

  // Handle null or invalid scheduledFor
  if (!record.scheduledFor) {
    return <span style={{ fontWeight: 'bold' }}>ASAP</span>;
  }

  const now = new Date();
  const scheduledFor = new Date(record.scheduledFor);

  // Check if date is valid
  if (Number.isNaN(scheduledFor.getTime())) {
    return <span style={{ fontWeight: 'bold' }}>ASAP</span>;
  }

  const diffMs = scheduledFor.getTime() - now.getTime();

  if (diffMs <= 0) {
    return <span style={{ fontWeight: 'bold' }}>ASAP</span>;
  }

  // Format duration
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return (
      <span style={{ color: '#388e3c' }}>
        {days} day{days > 1 ? 's' : ''}
      </span>
    );
  } else if (hours > 0) {
    return (
      <span style={{ color: '#388e3c' }}>
        {hours} hour{hours > 1 ? 's' : ''}
      </span>
    );
  } else if (minutes > 0) {
    return (
      <span style={{ color: '#f57500' }}>
        {minutes} minute{minutes > 1 ? 's' : ''}
      </span>
    );
  } else {
    return (
      <span style={{ color: '#f57500' }}>
        {seconds} second{seconds > 1 ? 's' : ''}
      </span>
    );
  }
};
RunInField.displayName = 'Run in';

// Custom component to display "Added" time with priority indicator
const AddedField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const formattedDate = formatDateTime(record.createdAt);

  // Add 🔺 indicator for priority jobs
  const priorityIndicator = record.priority ? '🔺 ' : '';

  return (
    <span>
      {priorityIndicator}
      {formattedDate}
    </span>
  );
};
AddedField.displayName = 'Added';

// Custom component to display attempt count (blank for first try, number for retries)
const AttemptField = () => {
  const record = useRecordContext();
  if (!record) return null;

  // Check if this is a retry by looking at retry_count in metadata or lastError
  const retryCount = record.metadata?.retryCount || record.retryCount || 0;
  const hasError = !!record.lastError;

  // Show blank for first attempt (no retries and no error)
  // Show attempt number for retries
  if (retryCount === 0 && !hasError) {
    return <span>—</span>;
  }

  // For retries, show the retry number (retry_count + 1 since we count from 0)
  if (retryCount > 0) {
    return <span>{retryCount + 1}</span>;
  }

  // Has error but no retry yet - show 1
  return <span>1</span>;
};
AttemptField.displayName = 'Attempt';

// Custom component to display trigger/context
const TriggerField = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (record.trigger === 'lifecycle') {
    return <span>Lifecycle</span>;
  } else if (record.trigger === 'retry') {
    const reason = record.metadata?.retryReason || 'unknown';
    return <span>Retry ({reason})</span>;
  } else if (record.trigger === 'manual') {
    return <span>Manual</span>;
  }

  // Show empty string for null/undefined trigger (backwards compatibility)
  return <span>{record.trigger || ''}</span>;
};
TriggerField.displayName = 'Trigger';

// Duration field for finished jobs - shows stored duration_ms
const DurationField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return <span>{formatDuration(record.durationMs)}</span>;
};
DurationField.displayName = 'Duration';

// Status field for finished jobs with colored badges
const FinishedStatusField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusColors: Record<string, string> = {
    completed: '#4caf50',
    failed: '#f44336',
    cancelled: '#9e9e9e',
  };

  const color = statusColors[record.status] || '#666';

  return <span style={{ color }}>{record.status}</span>;
};
FinishedStatusField.displayName = 'Status';

// Custom empty component for queue - shows friendly message instead of "No Process queue with names found"
const QueueEmpty = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      color: 'text.secondary',
    }}
  >
    <Typography variant="body1">No jobs in queue</Typography>
  </Box>
);

// Logs actions component
const HistoryActions = () => {
  const [isClearing, setIsClearing] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const { filterValues, setFilters } = useListContext();
  const hasActiveFilters = Object.keys(filterValues).length > 0;

  const handleClearHistory = async () => {
    setIsClearing(true);
    try {
      await clearProcessLogs();
      notify('Process log history cleared successfully', { type: 'success' });
      refresh();
      setClearDialogOpen(false);
    } catch (error) {
      logger.error('Clear history error', { error });
      notify('Failed to clear process log history', { type: 'error' });
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({}, filterValues);
  };

  return (
    <>
      <PageToolbar
        right={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {hasActiveFilters && (
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={() => setClearDialogOpen(true)}
              disabled={isClearing}
            >
              Clear Logs
            </Button>
          </Box>
        }
      />

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        aria-labelledby="clear-history-dialog-title"
        aria-describedby="clear-history-dialog-description"
      >
        <DialogTitle id="clear-history-dialog-title">
          Clear Process Logs
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-history-dialog-description">
            Are you sure you want to clear all process logs? This will
            permanently delete all process logs from the system. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleClearHistory}
            color="error"
            variant="contained"
          >
            Clear Logs
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Logs tab component - historical process events with filtering
const LogsTab = () => (
  <List
    filters={ProcessLogFilters}
    sort={{ field: 'id', order: 'DESC' }}
    perPage={25}
    title=""
    resource="process_logs_with_names"
    actions={<HistoryActions />}
    sx={{
      '& .RaList-main': {
        '& form': {
          flexWrap: 'nowrap',
          '@media (max-width: 900px)': {
            flexDirection: 'column',
            flexWrap: 'wrap',
          },
        },
      },
    }}
  >
    <Datagrid bulkActionButtons={false}>
      <DateTimeField source="createdAt" sortable />
      <EntityField label="Entity" />
      <TextField source="process" label="Process" sortable />
      <EventTypeField label="Event Type" sortBy="eventType" />
      <MessageField label="Message" />
    </Datagrid>
  </List>
);

// Queue actions with toggle component
interface QueueActionsWithToggleProps {
  view: QueueView;
  // eslint-disable-next-line no-unused-vars
  onViewChange: (view: QueueView) => void;
}

const QueueActionsWithToggle = ({
  view,
  onViewChange,
}: QueueActionsWithToggleProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleManualProcess = async () => {
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-queue');

      if (error) {
        throw error;
      }

      notify(`Queue processed: ${data?.processed || 0} jobs completed`, {
        type: 'success',
      });
      refresh();
    } catch (error) {
      logger.error('Manual process error', { error });
      notify('Failed to process queue', { type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearQueue = async () => {
    setIsClearing(true);
    try {
      await clearAllQueueJobs();

      notify('Queue cleared successfully', { type: 'success' });
      refresh();
    } catch (error) {
      logger.error('Clear queue error', { error });
      notify('Failed to clear queue', { type: 'error' });
    } finally {
      setIsClearing(false);
      setClearDialogOpen(false);
    }
  };

  return (
    <>
      {/* Custom toolbar layout - not using PageToolbar for full control */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          py: 1,
          px: 2,
        }}
      >
        {/* Left: Toggle buttons */}
        <ButtonGroup size="small" aria-label="queue view toggle">
          <Button
            onClick={() => onViewChange('active')}
            variant={view === 'active' ? 'contained' : 'outlined'}
          >
            Active
          </Button>
          <Button
            onClick={() => onViewChange('finished')}
            variant={view === 'finished' ? 'contained' : 'outlined'}
          >
            Finished
          </Button>
        </ButtonGroup>

        {/* Right: Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleManualProcess}
            disabled={isProcessing}
          >
            {isProcessing ? 'Running...' : 'Run'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setClearDialogOpen(true)}
            disabled={isClearing}
          >
            {isClearing ? 'Clearing...' : 'Clear'}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title">Clear Queue</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-dialog-description">
            Are you sure you want to clear all pending jobs from the queue? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleClearQueue} color="error" variant="contained">
            Clear Queue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Queue view type for toggle
type QueueView = 'active' | 'finished';

// Active queue datagrid (pending, processing, scheduled)
const ActiveQueueDatagrid = () => (
  <Datagrid bulkActionButtons={false} empty={<QueueEmpty />}>
    <AddedField label="Added" sortBy="createdAt" />
    <RunInField label="Scheduled" sortBy="scheduledFor" />
    <EntityField label="Entity" />
    <TextField source="process" label="Process" sortable />
    <TriggerField label="Trigger" sortBy="trigger" />
    <TextField source="status" label="Status" sortable />
    <AttemptField />
    <CancelJobButton />
    <JobActionsMenu />
  </Datagrid>
);

// Finished queue datagrid (completed, failed, cancelled)
const FinishedQueueDatagrid = () => (
  <Datagrid bulkActionButtons={false} empty={<QueueEmpty />}>
    <AddedField label="Added" sortBy="createdAt" />
    <EntityField label="Entity" />
    <TextField source="process" label="Process" sortable />
    <TriggerField label="Trigger" sortBy="trigger" />
    <FinishedStatusField label="Status" sortBy="status" />
    <DurationField label="Duration" sortBy="durationMs" />
    <AttemptField />
    <JobActionsMenu />
  </Datagrid>
);

// Queue tab component with Active/Finished toggle
const QueueTab = () => {
  const [view, setView] = useState<QueueView>('active');

  const activeStatuses = ['pending', 'processing', 'scheduled'];
  const finishedStatuses = ['completed', 'failed', 'cancelled'];

  const currentStatuses = view === 'active' ? activeStatuses : finishedStatuses;
  const sortField = view === 'active' ? 'scheduledFor' : 'createdAt';
  const sortOrder = view === 'active' ? 'ASC' : 'DESC'; // Active: next to run first, Finished: newest first

  return (
    <>
      {/* Toolbar rendered OUTSIDE List for full layout control */}
      <QueueActionsWithToggle view={view} onViewChange={setView} />

      <List
        sort={{ field: sortField, order: sortOrder }}
        perPage={25}
        title=""
        resource="process_queue_with_names"
        filter={{ status: currentStatuses }}
        actions={false}
        empty={false}
        queryOptions={{ refetchInterval: 2000 }}
      >
        {view === 'active' ? (
          <ActiveQueueDatagrid />
        ) : (
          <FinishedQueueDatagrid />
        )}
      </List>
    </>
  );
};

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`process-tabpanel-${index}`}
    aria-labelledby={`process-tab-${index}`}
    {...other}
  >
    {value === index && children}
  </div>
);

// Main component with tabs
export const ProcessLogList = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="process tabs"
        >
          <Tab
            label="Queue"
            id="process-tab-0"
            aria-controls="process-tabpanel-0"
          />
          <Tab
            label="Logs"
            id="process-tab-1"
            aria-controls="process-tabpanel-1"
          />
          <Tab
            label="Blob logs"
            id="process-tab-2"
            aria-controls="process-tabpanel-2"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <QueueTab />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <LogsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <BlobLogsTab />
      </TabPanel>
    </Box>
  );
};

export default ProcessLogList;
